import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMars, faVenus, faSort, faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";

// ===== RaceResult API config (JMM 2026, event 395147) =====
const RR_EVENT_ID = "395147";
const RR_LIST_NAME = "Results|Final results live";
const RR_CONFIG_URL = `https://my.raceresult.com/${RR_EVENT_ID}/RRPublish/data/config?page=results&noVisitor=1`;
const FALLBACK_CONTESTS = { 1: "45 K", 2: "25 K", 3: "10 K" };

const RR_CERTIFICATE_URL = (pid) =>
  `https://my-hk-1.raceresult.com/${RR_EVENT_ID}/details1/certificate/${pid}/JMM`;

// ===== Helpers (mirror Results.jsx style) =====
const capitalizeWords = (str) => {
  if (!str || typeof str !== "string") return "";
  return str
    .trim()
    .toLowerCase()
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const stripImg = (s) => {
  if (!s) return "";
  const m = String(s).match(/flags\/([A-Z]{2})\.svg/i);
  return m ? m[1].toLowerCase() : "";
};

const ISO_TO_NAME = {
  id: "Indonesia",
  in: "India",
  cn: "China",
  my: "Malaysia",
  jp: "Japan",
  kr: "South Korea",
  th: "Thailand",
  sg: "Singapore",
  ph: "Philippines",
  bn: "Brunei",
  au: "Australia",
  us: "USA",
  gb: "United Kingdom",
  de: "Germany",
  fr: "France",
  it: "Italy",
  es: "Spain",
  nl: "Netherlands",
  ch: "Switzerland",
  se: "Sweden",
  no: "Norway",
  be: "Belgium",
  ru: "Russia",
  tw: "Taiwan",
  by: "Belarus",
  cz: "Czech Republic",
  ma: "Morocco",
  eg: "Egypt",
  sx: "Sint Maarten (Dutch part)",
};

const isoToName = (iso) => ISO_TO_NAME[iso] || (iso ? iso.toUpperCase() : "-");

const formatGender = (gender) => {
  if (!gender) return "-";
  if (gender === "M")
    return (
      <>
        <FontAwesomeIcon icon={faMars} className="mr-1" /> Male
      </>
    );
  if (gender === "F")
    return (
      <>
        <FontAwesomeIcon icon={faVenus} className="mr-1" /> Female
      </>
    );
  return gender;
};

const parseGroupKey = (key) => {
  const m = String(key).match(/^#\d+_(.+)$/);
  return m ? m[1] : String(key);
};

const stripOrdinal = (s) => parseInt(String(s || "").replace(/\D/g, ""), 10) || null;

// ===== Components mirroring Results.jsx =====
const SkeletonRow = React.memo(() => (
  <tr className="border-b border-gray-200">
    {Array(7)
      .fill()
      .map((_, index) => (
        <td key={index} className="px-4 py-4">
          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
        </td>
      ))}
  </tr>
));

const CategoryButton = React.memo(({ category, selectedCategory, onClick }) => (
  <button
    onClick={() => onClick(category.distance)}
    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
      selectedCategory === category.distance
        ? "bg-slate-700 text-white"
        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
    }`}
    aria-label={`Filter results by category ${category.displayName}`}
  >
    {category.displayName}
  </button>
));

const Top3Table = ({ title, results }) => (
  <div className="w-full md:w-[calc(50%-0.5rem)] bg-white rounded-xl border border-gray-300 overflow-x-auto">
    <h2 className="text-lg font-semibold text-slate-800 mb-4 px-4 pt-4">{title}</h2>
    <table className="min-w-full table-auto border-collapse">
      <thead className="bg-gray-50 border-y border-gray-300">
        <tr>
          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[60px]">
            Rank
          </th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Time
          </th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[150px]">
            Name
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {results.length === 0 ? (
          <tr>
            <td colSpan={3} className="px-4 py-2 text-center text-sm text-gray-600">
              No data available
            </td>
          </tr>
        ) : (
          results.map((r, i) => (
            <tr key={`top3-${r.id || i}`} className="hover:bg-gray-50 transition-colors">
              <td className="px-2 py-2 text-sm text-gray-600 text-center w-[50px]">
                {r.genderRanking || "-"}
              </td>
              <td className="px-4 py-2 text-sm text-gray-600 font-bold w-[100px]">
                {r.time || "-"}
              </td>
              <td className="px-4 py-2 text-sm text-gray-600 uppercase max-w-[150px] truncate">
                {capitalizeWords(r.displayName)?.toUpperCase() || "-"}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

// ===== Data fetching =====
async function fetchConfig() {
  const res = await fetch(RR_CONFIG_URL, { cache: "no-store" });
  if (!res.ok) throw new Error("Failed to fetch RaceResult config");
  return res.json();
}

async function fetchList({ server, key, contestLabel }) {
  const params = new URLSearchParams({
    key,
    listname: RR_LIST_NAME,
    contest: "0",
    r: "all",
    l: "0",
  });
  // RaceResult list has a built-in groupFilter on contest name. Without
  // "f", only the default group (first contest with finishers) is returned.
  // Pass the contest label to get rows for that specific contest.
  if (contestLabel) params.set("f", contestLabel);
  const res = await fetch(
    `https://${server}/${RR_EVENT_ID}/results/list?${params.toString()}`,
    { cache: "no-store" }
  );
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`List fetch failed (${res.status}): ${txt.slice(0, 120)}`);
  }
  return res.json();
}

const rowToObject = (row, fields) => {
  const o = {};
  fields.forEach((f, i) => (o[f] = row[i]));
  return o;
};

// ===== Main component =====
export const ResultsJMMLive = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [dataByContest, setDataByContest] = useState({}); // { '10 K': json, ... }
  const [selectedCategory, setSelectedCategory] = useState(""); // contest id as string
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const timerRef = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // 1. Load config once
  useEffect(() => {
    fetchConfig()
      .then((cfg) => {
        if (!isMounted.current) return;
        setConfig(cfg);
        const ids = Object.keys(cfg.contests || FALLBACK_CONTESTS);
        if (ids.length) setSelectedCategory(ids[0]);
      })
      .catch((e) => isMounted.current && setError(e.message));
  }, []);

  // 2. Load list for one contest label
  const loadContest = useCallback(
    async (contestLabel) => {
      if (!config || !contestLabel) return;
      try {
        const json = await fetchList({
          server: config.server,
          key: config.key,
          contestLabel,
        });
        if (!isMounted.current) return;
        setDataByContest((prev) => ({ ...prev, [contestLabel]: json }));
        setLastUpdated(new Date());
        setError("");
      } catch (e) {
        if (isMounted.current) setError(e.message);
      }
    },
    [config]
  );

  // 3. Initial load: fetch every contest once in parallel
  useEffect(() => {
    if (!config) return;
    const labels = Object.values(config.contests || FALLBACK_CONTESTS).map(
      (l) => String(l).trim()
    );
    setLoading(true);
    Promise.all(labels.map((l) => loadContest(l))).finally(() => {
      if (isMounted.current) setLoading(false);
    });
  }, [config, loadContest]);

  // 4. Live polling: refresh only the active contest
  useEffect(() => {
    if (!config || !selectedCategory) return;
    const contests = config.contests || FALLBACK_CONTESTS;
    const activeLabel = String(contests[selectedCategory] || "").trim();
    if (!activeLabel) return;
    const sample = Object.values(dataByContest)[0];
    const interval = (sample?.LiveUpdateInterval || 30) * 1000;
    timerRef.current = setInterval(
      () => loadContest(activeLabel),
      Math.max(10000, interval)
    );
    return () => clearInterval(timerRef.current);
  }, [config, selectedCategory, dataByContest, loadContest]);

  const contests = config?.contests || FALLBACK_CONTESTS;

  // Build categories array (mimicking Results.jsx categories shape)
  const categories = useMemo(
    () =>
      Object.entries(contests).map(([id, label]) => ({
        distance: String(id),
        displayName: `JMM ${String(label).replace(/\s+/g, "")}`,
        categoryName: String(label).trim(),
        contestLabel: String(label).trim(),
      })),
    [contests]
  );

  // Flatten rows from all loaded contests.
  // RaceResult sometimes falls back to the default group when an unknown
  // "f" filter is sent (e.g. 45 K with no finishers yet returns 10 K data),
  // which would cause duplicate rows. Guard against that by only keeping
  // rows whose contest label matches the cache key they came from.
  const allRows = useMemo(() => {
    const norm = (s) => String(s || "").replace(/\s+/g, "").toLowerCase();
    const out = [];
    Object.entries(dataByContest).forEach(([label, data]) => {
      if (!data) return;
      const fields = data.DataFields || [];
      const expected = norm(label);
      Object.entries(data.data || {}).forEach(([outerKey, inner]) => {
        const contestLabel = parseGroupKey(outerKey) || label;
        if (norm(contestLabel) !== expected) return; // drop fallback bleed-over
        if (!inner) return;
        if (Array.isArray(inner)) {
          inner.forEach((r) => {
            const obj = rowToObject(r, fields);
            out.push({ ...obj, _contestLabel: contestLabel, _gender: "" });
          });
        } else {
          Object.entries(inner).forEach(([innerKey, list]) => {
            const genderLabel = parseGroupKey(innerKey);
            const gender = genderLabel.toLowerCase().startsWith("f")
              ? "F"
              : genderLabel.toLowerCase().startsWith("m")
              ? "M"
              : "";
            (list || []).forEach((r) => {
              const obj = rowToObject(r, fields);
              out.push({ ...obj, _contestLabel: contestLabel, _gender: gender });
            });
          });
        }
      });
    });
    // Dedupe by pid within a contest as a final safety net.
    const seen = new Set();
    return out.filter((r) => {
      const k = `${r._contestLabel}::${r.ID || r.BIB}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [dataByContest]);

  // Resolve currently selected contest label
  const selectedCategoryMeta = categories.find(
    (c) => c.distance === selectedCategory
  );
  const selectedContestLabel = selectedCategoryMeta?.contestLabel || "";

  // Map raw rows to standardized result objects (matching Results.jsx fields)
  const mapped = useMemo(() => {
    const norm = (s) => String(s || "").replace(/\s+/g, "").toLowerCase();
    const target = norm(selectedContestLabel);
    return allRows
      .filter((r) => (target ? norm(r._contestLabel) === target : true))
      .map((r) => {
        const flagIso = stripImg(r["NATION.FLAG"]);
        const status = String(r.StatusFinish || "").trim() || "DNF";
        const finished = status.toLowerCase() === "finished";
        return {
          id: String(r.ID || r.BIB || ""),
          pid: String(r.ID || ""),
          bibNumber: String(r.BIB || ""),
          displayName: String(r.DisplayName || "").trim(),
          gender: r._gender || "",
          nationalityIso: flagIso,
          nationality: isoToName(flagIso),
          time: finished ? String(r.FinishResultTime || r.NetTime || "") : "",
          status,
          finished,
          displayRanking:
            stripOrdinal(r["WithStatus([TotalRank.th])"]) ||
            (finished ? "-" : "DNF"),
          genderRanking: stripOrdinal(r["MFRank.th"]) || "-",
        };
      });
  }, [allRows, selectedContestLabel]);

  // Sortable columns. Default sort = displayRanking ascending (finishers first).
  const [sortBy, setSortBy] = useState("rank"); // 'rank' | 'gender' | 'genderRank'
  const [sortDir, setSortDir] = useState("asc"); // 'asc' | 'desc'

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const raceResults = useMemo(() => {
    const finishers = mapped.filter((r) => r.finished);
    const dnfs = mapped.filter((r) => !r.finished);

    const sorted = [...finishers].sort((a, b) => {
      let cmp = 0;
      if (sortBy === "gender") {
        // Female first when asc, then male; empty last
        const order = (g) => (g === "F" ? 0 : g === "M" ? 1 : 2);
        cmp = order(a.gender) - order(b.gender);
        if (cmp === 0) {
          const ar =
            typeof a.genderRanking === "number" ? a.genderRanking : 9999;
          const br =
            typeof b.genderRanking === "number" ? b.genderRanking : 9999;
          cmp = ar - br;
        }
      } else if (sortBy === "genderRank") {
        const ar =
          typeof a.genderRanking === "number" ? a.genderRanking : 9999;
        const br =
          typeof b.genderRanking === "number" ? b.genderRanking : 9999;
        cmp = ar - br;
      } else {
        const ar = typeof a.displayRanking === "number" ? a.displayRanking : 9999;
        const br = typeof b.displayRanking === "number" ? b.displayRanking : 9999;
        cmp = ar - br;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });

    return [...sorted, ...dnfs];
  }, [mapped, sortBy, sortDir]);

  // Top 3 male / female
  const top3Male = useMemo(
    () =>
      mapped
        .filter((r) => r.finished && r.gender === "M")
        .sort((a, b) => (a.genderRanking || 9999) - (b.genderRanking || 9999))
        .slice(0, 3),
    [mapped]
  );
  const top3Female = useMemo(
    () =>
      mapped
        .filter((r) => r.finished && r.gender === "F")
        .sort((a, b) => (a.genderRanking || 9999) - (b.genderRanking || 9999))
        .slice(0, 3),
    [mapped]
  );

  const handleCategoryChange = (distance) => setSelectedCategory(distance);

  const handleNameClick = (result) => {
    const name =
      capitalizeWords(result.displayName)?.toUpperCase() || "-";
    navigate(
      `/results/JMM/${selectedCategory}/${encodeURIComponent(name)}`,
      {
        state: {
          eventId: "JMM",
          name,
          ranking: result.displayRanking || "-",
          category: selectedCategoryMeta?.categoryName || "-",
          categoryDisplay: selectedCategoryMeta?.displayName || "-",
          startDateTime: "-",
          bibNumber: result.bibNumber || "-",
          nationality: result.nationality || "-",
          gender: result.gender || "-",
          status: result.finished ? "Finished" : result.status || "DNF",
          time: result.time || "-",
          genderRanking: result.genderRanking || "-",
          certificateUrl: result.pid ? RR_CERTIFICATE_URL(result.pid) : "",
          backTo: "/results/JMM",
        },
      }
    );
  };

  const formatTimeAgo = (d) => {
    if (!d) return "-";
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 5) return "just now";
    if (diff < 60) return `${diff}s ago`;
    return `${Math.floor(diff / 60)}m ago`;
  };

  return (
    <section className="min-h-screen py-12 sm:py-20 bg-white">
      <div className="container mx-auto px-3 sm:px-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800">JMM Race Results</h1>
          {error && (
            <span className="text-xs text-red-600 ml-auto">
              ⚠ {error}
            </span>
          )}
        </div>

        <div className="mb-6 flex flex-wrap gap-2">
          {categories.map((category) => (
            <CategoryButton
              key={category.distance}
              category={category}
              selectedCategory={selectedCategory}
              onClick={handleCategoryChange}
            />
          ))}
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <Top3Table title="Top 3 Men" results={top3Male} />
          <Top3Table title="Top 3 Women" results={top3Female} />
        </div>

        {/* Mobile card list */}
        <div className="sm:hidden flex flex-col gap-2">
          {loading ? (
            <div className="bg-white rounded-xl border border-gray-300 p-4 text-center text-sm text-gray-500">
              Loading…
            </div>
          ) : raceResults.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-300 p-4 text-center text-sm text-gray-600">
              No race results available for this category.
            </div>
          ) : (
            raceResults.map((result, index) => {
              const genderChar = String(result.gender || "").toUpperCase();
              const genderSymbol =
                genderChar === "M" ? "♂" : genderChar === "F" ? "♀" : "";
              return (
                <div
                  key={`m-${result.id || "u"}-${index}`}
                  className="bg-white rounded-xl border border-gray-300 overflow-hidden"
                  onClick={() => handleNameClick(result)}
                >
                  {/* Row 1: rank + name + time */}
                  <div className="flex items-center gap-3 px-3 py-3">
                    <span className="flex-none w-7 h-7 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold flex items-center justify-center">
                      {result.displayRanking || "-"}
                    </span>
                    <span className="flex-1 min-w-0 text-[13px] font-bold text-gray-900 uppercase truncate">
                      {capitalizeWords(result.displayName)?.toUpperCase() || "-"}
                    </span>
                    <span className="flex-none text-[13px] text-gray-700 tabular-nums">
                      {result.time || "-"}
                    </span>
                  </div>
                  {/* Divider */}
                  <div className="border-t border-gray-200" />
                  {/* Row 2: flag + bib + gender + gender rank */}
                  <div className="flex items-center gap-2 px-3 py-2.5 text-[12px] text-gray-600">
                    {result.nationalityIso ? (
                      <img
                        src={`https://flagicons.lipis.dev/flags/4x3/${result.nationalityIso}.svg`}
                        className="flex-none w-5 h-3.5 rounded-sm border border-gray-200"
                        alt={result.nationality}
                      />
                    ) : (
                      <span className="flex-none w-5 h-3.5" />
                    )}
                    <span className="text-gray-700">
                      BIB {result.bibNumber || "-"}
                    </span>
                    {genderSymbol ? (
                      <span
                        className={
                          genderChar === "F" ? "text-pink-500" : "text-blue-500"
                        }
                      >
                        {genderSymbol}
                      </span>
                    ) : null}
                    <span className="ml-auto text-gray-500">
                      Gender Rank:{" "}
                      <span className="font-semibold text-gray-800">
                        {result.genderRanking || "-"}
                      </span>
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden sm:block bg-white rounded-xl border border-gray-300 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-auto">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-3 text-center text-[11px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[50px]">
                  Rank
                </th>
                <th className="px-2 sm:px-4 py-3 text-left text-[11px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[80px] sm:w-[100px]">
                  Time
                </th>
                <th className="px-2 sm:px-4 py-3 text-left text-[11px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[150px]">
                  Name
                </th>
                <th className="hidden sm:table-cell px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bib
                </th>
                <th className="hidden md:table-cell px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[150px]">
                  Country
                </th>
                <th
                  className="px-2 sm:px-4 py-3 text-center text-[11px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[120px] cursor-pointer select-none hover:text-slate-800"
                  onClick={() => toggleSort("gender")}
                >
                  Gender{" "}
                  <FontAwesomeIcon
                    icon={
                      sortBy === "gender"
                        ? sortDir === "asc"
                          ? faSortUp
                          : faSortDown
                        : faSort
                    }
                    className="ml-1 text-[10px]"
                  />
                </th>
                <th className="px-2 sm:px-4 py-3 text-center text-[11px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[90px]">
                  <span className="whitespace-nowrap">
                    <span className="hidden sm:inline">Gender Rank</span>
                    <span className="sm:hidden">G. Rank</span>
                  </span>
                </th>
                <th className="px-2 sm:px-4 py-3 text-center text-[11px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <SkeletonRow />
              ) : raceResults.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-4 text-center text-sm text-gray-600"
                  >
                    No race results available for this category.
                  </td>
                </tr>
              ) : (
                raceResults.map((result, index) => (
                  <tr
                    key={`${result.id || "u"}-${index}`}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-2 py-3 sm:py-4 text-sm text-gray-600 text-center w-[50px]">
                      {result.displayRanking || "-"}
                    </td>
                    <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-600 font-bold">
                      {result.time || "-"}
                    </td>
                    <td
                      className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-600 uppercase max-w-[120px] sm:max-w-[150px] truncate cursor-pointer hover:text-slate-900"
                      onClick={() => handleNameClick(result)}
                    >
                      <span className="block truncate">
                        {capitalizeWords(result.displayName)?.toUpperCase() || "-"}
                      </span>
                      {/* Mobile: show BIB & flag inline below name */}
                      <span className="sm:hidden mt-1 flex items-center gap-2 text-[10px] text-gray-500 normal-case">
                        {result.bibNumber ? (
                          <span className="border border-gray-300 rounded-full px-1.5 py-0.5">
                            BIB {result.bibNumber}
                          </span>
                        ) : null}
                        {result.nationalityIso ? (
                          <img
                            src={`https://flagicons.lipis.dev/flags/4x3/${result.nationalityIso}.svg`}
                            className="w-4 h-3 border border-gray-300 rounded"
                            alt={result.nationality}
                          />
                        ) : null}
                      </span>
                    </td>
                    <td className="hidden sm:table-cell px-4 py-4 whitespace-nowrap text-sm text-gray-600 text-center">
                      <span className="border border-gray-300 rounded-full px-2 py-1">
                        {result.bibNumber ? `BIB ${result.bibNumber}` : "-"}
                      </span>
                    </td>
                    <td className="hidden md:table-cell px-4 py-4 whitespace-nowrap text-sm text-gray-600 max-w-[150px] text-center">
                      <span className="border border-gray-300 rounded-full px-2 py-1 inline-flex items-center">
                        {result.nationalityIso ? (
                          <img
                            src={`https://flagicons.lipis.dev/flags/4x3/${result.nationalityIso}.svg`}
                            className="country-flag w-4 h-3 mr-1 border border-gray-300 rounded"
                            alt={result.nationality}
                          />
                        ) : null}
                        {result.nationality || "-"}
                      </span>
                    </td>
                    <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-600 max-w-[120px] text-center">
                      <span className="border border-gray-300 rounded-full px-2 py-1 inline-block">
                        {formatGender(result.gender)}
                      </span>
                    </td>
                    <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-600 text-center">
                      {result.genderRanking || "-"}
                    </td>
                    <td
                      className={`px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-xs text-center ${
                        result.finished ? "text-green-700" : "text-red-700"
                      }`}
                    >
                      <span
                        className={`${
                          result.finished
                            ? "bg-green-50 border-green-400"
                            : "bg-red-50 border-red-400"
                        } border rounded-full px-2 py-1`}
                      >
                        {result.finished ? "Finished" : result.status || "DNF"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default ResultsJMMLive;
